import { Injectable, Injector, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, take, tap } from 'rxjs/operators';
import { IrisFoldersService } from '@iris/common/services/docs/folders.service';
import { Observable } from 'rxjs';
import { IrisFileFilterEntityI } from '@iris/common/modules/dms/models/IrisDmsFileFilterEntity';
import { IrisDmsContentPageFilterI } from '../models/IrisDmsContentPagination';
import { IrisQueryParams, IrisQueryParamsBuilder } from '@iris/api-query';
import { IrisFilterPropertyMeta, dateRangeFilterMeta, textFilterMeta, userFilterMeta } from '@iris/modules/module-filters-v2/filter-meta';
import { TIMEZONE } from '@iris/common/tokens';
import { defaultChipRenderingOptions } from '@iris/modules/module-filters-v2/chips/rendering-options';
import { filterValueArrayIsEmpty, filterValueCommonIsEmpty } from '@iris/modules/module-filters-v2/utils';
import { IrisDmsTagsStore } from '../store/tags.signal-store';
import { toObservable } from '@angular/core/rxjs-interop';

export interface IrisFilterInProgressValueI {
  id: number;
  name: string;
  value: string;
}

@Injectable({ providedIn: 'root' })
export class IrisDmsFilterService {
  private readonly translate = inject(TranslateService);
  private readonly foldersService = inject(IrisFoldersService);
  private readonly injector = inject(Injector);
  private readonly timezone$ = inject<Observable<string>>(TIMEZONE);

  private readonly dmsTagsStore = inject(IrisDmsTagsStore);

  private readonly dmsFilterMeta = signal<IrisFilterPropertyMeta[]>([]);
  private readonly dmsDefaultFilter: IrisFileFilterEntityI = {
    text: null,
    name: null,
    content: null,
    isTextName: true,
    isTextContent: true,
    createdOn: {
      from: null,
      to: null,
    },
    recordingTime: {
      from: null,
      to: null,
    },
    tags: null,
    createdBy: null,
    folderId: null,
  };
  
  private readonly dmsModalPageFilterMeta = signal<IrisFilterPropertyMeta[]>([]);
  readonly dmsFilterProjectMeta = {
    propertyId: 'project',
    appendFilterQueryParamsFn: (builder: IrisQueryParamsBuilder, projects: number[]) => {
      const filteredProjects = projects.filter(Boolean);
      if (!filteredProjects.length) {
        return builder;
      }
      return builder.filter('project', filteredProjects);
    },
    filterValueIsEmpty: filterValueArrayIsEmpty,
  };

  private readonly tagsLoaded$ = toObservable(this.dmsTagsStore.loaded);

  constructor() {
    this.timezone$.pipe(
      take(1),
      tap(timezone => {
        this.dmsFilterMeta.set([
          textFilterMeta('name', 'label.search.InName'),
          textFilterMeta('content', 'label.search.InText'),
          userFilterMeta('createdBy', 'label.Creator'),
          dateRangeFilterMeta('createdOn', 'label.CreatedBetween', this.injector, timezone),
          dateRangeFilterMeta('recordingTime', 'label.dms.RecordingTime', this.injector, timezone),
          {
            propertyId: 'tags',
            label: 'label.Tags',
            appendFilterQueryParamsFn: (builder: IrisQueryParamsBuilder, tagsIds: number[]) => {
              return builder.urlParam('tags', tagsIds).filter('tagIds', tagsIds);
            },
            chipRenderingOptions: defaultChipRenderingOptions<number>(tagId => {
              return this.tagsLoaded$.pipe(
                filter(Boolean),
                map(() => this.dmsTagsStore.getTagById(tagId)?.name),
              );
            }),
            filterValueIsEmpty: filterValueArrayIsEmpty,
          },
          {
            propertyId: 'folderId',
            label: 'label.dms.Folders',
            appendFilterQueryParamsFn: (builder: IrisQueryParamsBuilder, foldersIds: string[]) => {
              return builder.filter('folderId', foldersIds);
            },
            chipRenderingOptions: defaultChipRenderingOptions<string>(folderId => {
              return this.foldersService.getFolderById(folderId).pipe(
                map((folder) => folder?.name),
              );
            }),
            filterValueIsEmpty: filterValueCommonIsEmpty,
          },
        ]);

        this.dmsModalPageFilterMeta.set([
          textFilterMeta('name', 'label.search.InName', 'name', false, false, 'fts'),
          dateRangeFilterMeta('updatedOn', 'label.CreatedBetween', this.injector, 'utc'),
        ]);
      }),
    ).subscribe();
  }

  extractModalPageFilter(filter: IrisDmsContentPageFilterI): IrisQueryParams {
    const filterKeys = Object.keys(filter ?? {});

    const queryBuilder = new IrisQueryParamsBuilder();

    this.dmsModalPageFilterMeta()
      .filter(property => filterKeys.includes(property.propertyId))
      .forEach(property => property.appendFilterQueryParamsFn(queryBuilder, filter[property.propertyId]));

    return queryBuilder.toStructure();
  }

  getFilterInProgressValues(): IrisFilterInProgressValueI[] {
    return [
      { id: 0, name: this.translate.instant('label.dms.FileInProgress'), value: 'true' },
      { id: 1, name: this.translate.instant('label.dms.FileAvailable'), value: 'false' },
    ];
  }

  getDmsFilterMeta(): IrisFilterPropertyMeta[] {
    return this.dmsFilterMeta();
  }

  getDefaultFilter(): IrisFileFilterEntityI {
    return this.dmsDefaultFilter;
  }
}
